import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  groupPermissionsByCategory,
  type GroupablePermission,
} from "@/components/ui/permissions/groupPermissions";

export type PermissionOption = GroupablePermission;

interface PermissionsMultiSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All available permissions, grouped by category in the dialog. */
  permissions: PermissionOption[];
  /** Currently-selected permission codes when the dialog opens. */
  initialSelected: string[];
  /** Title shown in the dialog header. */
  title: string;
  /** Optional description shown under the title. */
  description?: string;
  /** Called when the user clicks Save. Returns a promise so the dialog can show a spinner. */
  onSave: (selectedCodes: string[]) => Promise<void>;
}

/**
 * Dialog-based multi-select for permission codes. Permissions are grouped by
 * category with a per-category "select all" toggle, plus a free-text search
 * that filters across name, code, and description. The user's selection is
 * staged inside the dialog and committed via Save — Cancel discards.
 */
export function PermissionsMultiSelectDialog({
  open,
  onOpenChange,
  permissions,
  initialSelected,
  title,
  description,
  onSave,
}: PermissionsMultiSelectDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync the staged selection whenever the dialog opens with a new role.
  // This avoids stale state if the user closes & reopens the dialog after
  // edits elsewhere.
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected));
      setSearch("");
    }
  }, [open, initialSelected]);

  const grouped = useMemo(
    () => groupPermissionsByCategory(permissions, search),
    [permissions, search]
  );

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleCategory = (category: string, allCodes: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = allCodes.every((c) => next.has(c));
      if (allSelected) {
        allCodes.forEach((c) => next.delete(c));
      } else {
        allCodes.forEach((c) => next.add(c));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave([...selected].sort());
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    onOpenChange(next);
  };

  const totalShown = grouped.reduce((sum, g) => sum + g.permissions.length, 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-[640px]">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className="border-b px-6 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code, name, or description…"
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {selected.size} selected · {totalShown} shown · {permissions.length} total
            </span>
            {selected.size > 0 ? (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Clear selection
              </button>
            ) : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-6 py-4">
            {grouped.length === 0 ? (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No permissions match your search.
              </div>
            ) : (
              grouped.map(({ category, permissions: catPerms }, idx) => {
                const codes = catPerms.map((p) => p.code);
                const allSelected = codes.every((c) => selected.has(c));
                const someSelected = !allSelected && codes.some((c) => selected.has(c));

                return (
                  <div key={category} className={cn(idx > 0 && "mt-4")}>
                    <div className="sticky top-0 z-10 -mx-6 mb-2 flex items-center justify-between bg-background/95 px-6 py-1 backdrop-blur">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono uppercase">
                          {category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {codes.filter((c) => selected.has(c)).length}/{codes.length}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleCategory(category, codes)}
                        className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {allSelected ? "Deselect all" : someSelected ? "Select rest" : "Select all"}
                      </button>
                    </div>
                    <div className="space-y-1">
                      {catPerms.map((p) => {
                        const isSelected = selected.has(p.code);
                        return (
                          <label
                            key={p.code}
                            htmlFor={`perm-${p.code}`}
                            className={cn(
                              "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent",
                              isSelected
                                ? "border-primary/50 bg-primary/5"
                                : "border-border bg-card"
                            )}
                          >
                            <Checkbox
                              id={`perm-${p.code}`}
                              checked={isSelected}
                              onCheckedChange={() => toggle(p.code)}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                  {p.code}
                                </code>
                                {isSelected ? (
                                  <Check className="h-3.5 w-3.5 text-primary" />
                                ) : null}
                              </div>
                              <div className="mt-1 text-sm font-medium leading-tight">{p.name}</div>
                              {p.description ? (
                                <div className="mt-0.5 text-xs text-muted-foreground">
                                  {p.description}
                                </div>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {idx < grouped.length - 1 ? <Separator className="mt-4" /> : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              `Save ${selected.size} permission${selected.size === 1 ? "" : "s"}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
